import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  MessageSquare,
  ChevronDown,
  Clock,
  CheckCircle2,
  Loader2,
  Filter,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFeedback } from "@/providers/FeedbackProvider";
import { ClientFeedback, FeedbackCategory } from "@shared/types/FeedbackDto";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  OVERALL: "Overall",
  COMMUNICATION: "Communication",
  QUALITY: "Quality",
  TIMELINE: "Timeline",
  SUPPORT: "Support",
  PROCESS: "Process",
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function FeedbackCard({ feedback }: { feedback: ClientFeedback }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasResponse = !!feedback.response;

  return (
    <motion.div
      layout
      className={cn(
        "bg-card border border-border rounded-xl overflow-hidden transition-shadow",
        isExpanded && "ring-1 ring-primary/20"
      )}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="text-xs">
                {CATEGORY_LABELS[feedback.category]}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatDate(feedback.createdAt)}
              </span>
              {hasResponse ? (
                <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Responded
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4">
              {feedback.npsScore !== undefined && (
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      "text-lg font-bold",
                      feedback.npsScore >= 9
                        ? "text-green-500"
                        : feedback.npsScore >= 7
                        ? "text-amber-500"
                        : "text-red-500"
                    )}
                  >
                    {feedback.npsScore}
                  </span>
                  <span className="text-xs text-muted-foreground">NPS</span>
                </div>
              )}
              {feedback.satisfactionRating && (
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star
                      key={i}
                      className={cn(
                        "w-4 h-4",
                        i < feedback.satisfactionRating!
                          ? "fill-amber-400 text-amber-400"
                          : "text-muted-foreground/30"
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
            {feedback.comment && (
              <p
                className={cn(
                  "mt-2 text-sm text-muted-foreground",
                  !isExpanded && "line-clamp-2"
                )}
              >
                {feedback.comment}
              </p>
            )}
          </div>
          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}>
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          </motion.div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-border pt-4">
              {feedback.projectName && (
                <div className="mb-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    Related Project:
                  </span>
                  <span className="text-sm ml-2">{feedback.projectName}</span>
                </div>
              )}

              {feedback.comment && (
                <div className="mb-4">
                  <span className="text-xs font-medium text-muted-foreground block mb-1">
                    Your Feedback:
                  </span>
                  <p className="text-sm leading-relaxed">{feedback.comment}</p>
                </div>
              )}

              {hasResponse && (
                <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <MessageSquare className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm font-medium">
                      Response from {feedback.respondedBy}
                    </span>
                    {feedback.respondedAt && (
                      <span className="text-xs text-muted-foreground">
                        · {formatDate(feedback.respondedAt)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed pl-8">
                    {feedback.response}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FeedbackPage() {
  const { feedback, isLoading, openFeedbackModal } = useFeedback();
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredFeedback = feedback.filter((f) => {
    if (categoryFilter !== "all" && f.category !== categoryFilter) return false;
    if (statusFilter === "responded" && !f.response) return false;
    if (statusFilter === "pending" && f.response) return false;
    return true;
  });

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Your Feedback</h1>
          <p className="text-muted-foreground mt-1">
            Track your submitted feedback and responses
          </p>
        </div>
        <Button onClick={() => openFeedbackModal()}>
          <MessageSquare className="w-4 h-4 mr-2" />
          Share Feedback
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="responded">Responded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredFeedback.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No feedback yet</h3>
          <p className="text-muted-foreground mb-4">
            Share your experience to help us improve our services.
          </p>
          <Button onClick={() => openFeedbackModal()}>Share Feedback</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredFeedback.map((fb) => (
            <FeedbackCard key={fb.id} feedback={fb} />
          ))}
        </div>
      )}

      <div className="mt-8 text-center text-sm text-muted-foreground">
        {feedback.length} total feedback submissions
      </div>
    </div>
  );
}

